import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';

const cache = {};

/**
 * Polls an API endpoint on an interval. Skips work when document.hidden unless refetching on visibility.
 * @param {string} endpoint
 * @param {number} intervalMs
 * @param {Record<string, unknown>|undefined} params - query params (stable identity not required; stored in ref)
 * @param {{ enabled?: boolean }} [options]
 */
export function usePolling(endpoint, intervalMs = 60000, params, options = {}) {
  const { enabled = true } = options;
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const cacheKey = `${endpoint}:${JSON.stringify(params ?? {})}`;
  const [data, setData] = useState(() => cache[cacheKey]?.data ?? null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (document.hidden) return;
    try {
      const res = await api.get(endpoint, {
        params: paramsRef.current,
        withCredentials: true,
      });
      cache[cacheKey] = { data: res.data, ts: Date.now() };
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [endpoint, cacheKey]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const cached = cache[cacheKey];
    const isFresh = cached && Date.now() - cached.ts < intervalMs / 2;
    if (isFresh) {
      setData(cached.data);
    } else {
      fetchData();
    }

    timerRef.current = setInterval(fetchData, intervalMs);

    const handleVisibility = () => {
      if (!document.hidden) fetchData();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [endpoint, intervalMs, cacheKey, fetchData, enabled]);

  return { data, error, refetch: fetchData };
}
